import type {
  CRMUser,
  EnhancedDeal,
  PdfAnnotation,
  ProductionCommentAccess,
  RfqProject,
  RfqSubmission,
  SupplierOrg,
  TaskComment,
  TimberProject,
  UserRole,
} from '../types';
import { applyCrmTestUserMentionPolicy } from './mentionAffiliation';
import {
  buildProductionCommentAccessDoc,
  supplierMatchesAwardedWork,
} from './productionCommentAccess';

const STAFF_MENTION_INTERNAL_ROLES: ReadonlySet<UserRole> = new Set<UserRole>([
  'employee',
  'employee_shop',
  'employee_office',
  'admin',
  'designer',
  'architect',
  'engineer',
  'staff',
  'cpa',
]);

/** LCTF ops + design partners. CPAs and other suppliers stay off the supplier @ list. */
const SUPPLIER_VIEWER_MENTION_ROLES: ReadonlySet<UserRole> = new Set<UserRole>([
  'employee',
  'employee_shop',
  'employee_office',
  'admin',
  'staff',
  'designer',
  'architect',
  'engineer',
]);

/** Project comment / PDF authors who count as "commented elsewhere" for designer eligibility. */
export function collectProjectCommentAuthorIds(input: {
  dealId?: string | null;
  projectIds?: string[];
  taskComments?: Array<Pick<TaskComment, 'authorId' | 'dealId' | 'projectId' | 'groupCommentKey'>>;
  pdfAnnotations?: Array<Pick<PdfAnnotation, 'userId' | 'projectId' | 'replies'>>;
}): Set<string> {
  const authors = new Set<string>();
  const dealId = (input.dealId || '').trim();
  const projectIds = new Set((input.projectIds || []).filter(Boolean));

  for (const c of input.taskComments || []) {
    const authorId = (c.authorId || '').trim();
    if (!authorId) continue;
    const commentDeal = (c.dealId || '').trim()
      || (c.groupCommentKey?.includes('::') ? c.groupCommentKey.split('::')[0]!.trim() : '');
    const onDeal = !!dealId && (commentDeal === dealId);
    const onProject = !!c.projectId && projectIds.has(c.projectId);
    if (onDeal || onProject) authors.add(authorId);
  }

  for (const ann of input.pdfAnnotations || []) {
    if (ann.projectId && projectIds.has(ann.projectId) && ann.userId) {
      authors.add(ann.userId);
    }
    for (const reply of ann.replies || []) {
      if (ann.projectId && projectIds.has(ann.projectId) && reply.userId) {
        authors.add(reply.userId);
      }
    }
  }

  return authors;
}

/**
 * Production @mention candidates.
 * Staff viewers: internals + awarded supplier portal users (org-capability fallback).
 * Supplier viewers: LCTF employees/admins/staff plus designers and engineers.
 * CRM test accounts are mentionable only for admin viewers.
 */
function supplierUserEligibleForMention(input: {
  user: CRMUser;
  orgById: Map<string, SupplierOrg>;
  access: Pick<ProductionCommentAccess, 'timberOrgIds' | 'cncOrgIds'>;
  taskAssignedOrgIds: Set<string>;
}): boolean {
  const orgId = input.user.supplierOrgId?.trim();
  if (!orgId) return false;
  if (input.taskAssignedOrgIds.has(orgId)) return true;
  const org = input.orgById.get(orgId);
  return supplierMatchesAwardedWork({
    orgId,
    flags: input.user.supplierDesignations,
    orgCapabilities: org?.capabilities,
    access: input.access,
  });
}

export function buildProductionMentionCandidates(input: {
  viewerRole?: UserRole | string | null;
  crmUsers: CRMUser[];
  dealId?: string | null;
  deal?: EnhancedDeal | null;
  projectIds?: string[];
  productionCommentAccess?: ProductionCommentAccess[];
  rfqProjects?: RfqProject[];
  rfqSubmissions?: RfqSubmission[];
  timberProjects?: TimberProject[];
  supplierOrgs?: SupplierOrg[];
  /** Supplier org ids from hub task / onboarding `supplierOrgIds` (manual or RFQ-filled). */
  taskAssignedSupplierOrgIds?: string[];
  taskComments?: Array<Pick<TaskComment, 'authorId' | 'dealId' | 'projectId' | 'groupCommentKey'>>;
  pdfAnnotations?: Array<Pick<PdfAnnotation, 'userId' | 'projectId' | 'replies'>>;
}): CRMUser[] {
  const crmUsers = input.crmUsers || [];
  const dealId = (input.dealId || '').trim();
  const finalize = (users: CRMUser[]) =>
    applyCrmTestUserMentionPolicy(users, crmUsers, input.viewerRole);

  if (input.viewerRole === 'supplier') {
    return finalize(
      crmUsers.filter((u) => u.isActive && SUPPLIER_VIEWER_MENTION_ROLES.has(u.role)),
    );
  }

  const internals = crmUsers.filter(
    (u) => u.isActive && STAFF_MENTION_INTERNAL_ROLES.has(u.role),
  );
  if (!dealId) return finalize(internals);

  const access =
    (input.productionCommentAccess || []).find((row) => row.dealId === dealId)
    || buildProductionCommentAccessDoc({
      dealId,
      projects: input.rfqProjects || [],
      submissions: input.rfqSubmissions || [],
      hubProjects: input.timberProjects || [],
    });

  const orgById = new Map<string, SupplierOrg>();
  for (const org of input.supplierOrgs || []) {
    if (org.id) orgById.set(org.id, org);
  }

  const taskAssignedOrgIds = new Set(
    (input.taskAssignedSupplierOrgIds || []).map((id) => id.trim()).filter(Boolean),
  );

  const awarded = crmUsers.filter((u) => {
    if (!u.isActive || u.role !== 'supplier') return false;
    return supplierUserEligibleForMention({
      user: u,
      orgById,
      access,
      taskAssignedOrgIds,
    });
  });

  const seen = new Set(internals.map((u) => u.uid));
  return finalize([...internals, ...awarded.filter((u) => !seen.has(u.uid))]);
}
